
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SEOHead from '../../components/seo/SEOHead';
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "お問い合わせ - 紳士服のミッキー",
    "description": "紳士服のミッキーへのお問い合わせページ。商品に関するご質問やご相談はお気軽にどうぞ。",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
    "mainEntity": {
      "@type": "Organization",
      "name": "紳士服のミッキー",
      "telephone": "0284-41-8666",
      "email": "shinshifukumickey@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "相生町1-2",
        "addressLocality": "足利市",
        "addressRegion": "栃木県",
        "addressCountry": "JP"
      },
      "openingHours": "Mo-Su 09:30-18:00"
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3caehcef3sotc9j5ti0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'お電話でのお問い合わせ',
      content: '0284-41-8666',
      description: '営業時間：AM9時30分 ～ PM6時00分'
    },
    {
      icon: 'ri-mail-line',
      title: 'メールでのお問い合わせ',
      content: 'shinshifukumickey@gmail.com',
      description: '24時間受付（返信は営業時間内）'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <SEOHead
        title="お問い合わせ - 紳士服のミッキー | 商品・店舗に関するご質問"
        description="紳士服のミッキーへのお問い合わせページ。商品に関するご質問、在庫確認、サイズ相談など、お気軽にお問い合わせください。"
        keywords="お問い合わせ,紳士服のミッキー,商品質問,在庫確認,サイズ相談,足利市"
        ogTitle="お問い合わせ - 紳士服のミッキー"
        ogDescription="商品に関するご質問や店舗へのお問い合わせなど、お気軽にお問い合わせください。"
        structuredData={structuredData}
      />
      <Header />
      
      {/* ヒーローセクション */}
      <section className="py-20 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-6xl font-bold mb-6">
            お問い合わせ
          </h1>
          <p className="text-2xl max-w-3xl mx-auto">
            商品に関するご質問や店舗へのお問い合わせなど、<br />
            お気軽にお問い合わせください。
          </p>
        </div>
      </section>

      {/* 連絡先情報 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              お問い合わせ方法
            </h2>
            <p className="text-xl text-gray-600">
              お客様のご都合に合わせて、お好みの方法でお問い合わせください。
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {contactInfo.map((info, index) => (
              <div key={index} className="text-center p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${info.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-800">{info.title}</h3>
                <p className="text-xl font-semibold text-purple-600 mb-2">{info.content}</p>
                <p className="text-gray-600">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* お問い合わせフォーム */}
      <section className="py-20 bg-gradient-to-r from-purple-100 via-pink-100 to-orange-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              メールで問い合わせ
            </h2>
            <p className="text-xl text-gray-600">
              下記フォームにご記入いただき、送信してください。
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <form 
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-lg font-semibold text-gray-800 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300 text-sm"
                    placeholder="山田太郎"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-lg font-semibold text-gray-800 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300 text-sm"
                    placeholder="example@email.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-lg font-semibold text-gray-800 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300 text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-lg font-semibold text-gray-800 mb-2">
                    お問い合わせ種別 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300 text-sm appearance-none pr-8"
                    >
                      <option value="">選択してください</option>
                      <option value="商品について">商品について</option>
                      <option value="在庫確認">在庫確認</option>
                      <option value="サイズ相談">サイズ相談</option>
                      <option value="店舗について">店舗について</option>
                      <option value="その他">その他</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-8">
                <label htmlFor="message" className="block text-lg font-semibold text-gray-800 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors duration-300 text-sm resize-vertical"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </div>
              </div>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-xl">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-600 text-xl mr-2"></i>
                    <span className="text-green-800 font-semibold">お問い合わせを送信いたしました。ありがとうございます。</span>
                  </div>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-xl">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 text-xl mr-2"></i>
                    <span className="text-red-800 font-semibold">送信に失敗しました。もう一度お試しください。</span>
                  </div>
                </div>
              )}
              
              <div className="text-center">
                <Button 
                  type="submit" 
                  size="lg" 
                  className="text-xl px-12 py-4"
                  onClick={() => {}}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      送信する
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* よくある質問 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              よくあるご質問
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: '駐車場はありますか？',
                answer: '無料駐車場を6台分ご用意しております。'
              },
              {
                question: 'クレジットカードは使えますか？',
                answer: '各種クレジットカード、電子マネー、QRコード決済に対応しております。'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">
                    Q
                  </span>
                  {faq.question}
                </h3>
                <p className="text-gray-700 ml-11 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
